import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type DashboardFilterState = {
  territories: string[];
  fromYear: string;
  toYear: string;
  /** Snapshot year for Donut / Radar (defaults to toYear). */
  year: string;
  metric: "superficie" | "part";
  /** Area chart display: overlay vs stacked shares. */
  areaMode: "overlay" | "stacked";
  serviceIds: string[];
  indicateurIds: string[];
  /** Up to 3 territories for radar comparison. */
  radarTerritories: string[];
  dateFrom?: string;
  dateTo?: string;
};

function splitCsv(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function useDashboardFilters() {
  const [params, setParams] = useSearchParams();

  const state: DashboardFilterState = useMemo(() => {
    const toYear = params.get("to") ?? "2025";
    const dateFrom = params.get("dateFrom");
    const dateTo = params.get("dateTo");
    return {
      territories: splitCsv(params.get("territories")),
      fromYear: params.get("from") ?? "2005",
      toYear,
      year: params.get("year") ?? toYear,
      metric: params.get("metric") === "part" ? "part" : "superficie",
      areaMode: params.get("areaMode") === "stacked" ? "stacked" : "overlay",
      serviceIds: splitCsv(params.get("services")),
      indicateurIds: splitCsv(params.get("indicateurs")),
      radarTerritories: splitCsv(params.get("radar")).slice(0, 3),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
    };
  }, [params]);

  const setState = useCallback(
    (patch: Partial<DashboardFilterState>) => {
      const next = new URLSearchParams(params);
      const merged = { ...state, ...patch };
      if (merged.territories.length) next.set("territories", merged.territories.join(","));
      else next.delete("territories");
      if (merged.serviceIds.length) next.set("services", merged.serviceIds.join(","));
      else next.delete("services");
      if (merged.indicateurIds.length) next.set("indicateurs", merged.indicateurIds.join(","));
      else next.delete("indicateurs");
      if (merged.radarTerritories.length) {
        next.set("radar", merged.radarTerritories.slice(0, 3).join(","));
      } else next.delete("radar");
      next.set("from", merged.fromYear);
      next.set("to", merged.toYear);
      next.set("year", merged.year);
      next.set("metric", merged.metric);
      next.set("areaMode", merged.areaMode);
      if (merged.dateFrom) next.set("dateFrom", merged.dateFrom);
      else next.delete("dateFrom");
      if (merged.dateTo) next.set("dateTo", merged.dateTo);
      else next.delete("dateTo");
      setParams(next, { replace: true });
    },
    [params, setParams, state],
  );

  const reset = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const dateFrom = state.dateFrom ?? `${state.fromYear}-01-01`;
  const dateTo = state.dateTo ?? `${state.toYear}-12-31`;

  return { state, setState, reset, dateFrom, dateTo, activeCount: params.size };
}
